import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Download, ChevronDown } from "lucide-react";
import { hero, site } from "@/data/content";
import { Emblem } from "@/components/Emblem";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-fw-navy" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={hero.image} alt="Feuerwehr Dingharting im Einsatz" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-fw-navy via-fw-navy/50 to-fw-navy/30" />
        <div className="absolute inset-0 bg-fw-navy/25" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-16 md:px-8 md:pb-24">

        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-technical uppercase tracking-[0.35em] text-fw-gold text-sm md:text-base"
        >
          {hero.kicker}
        </motion.span>

        <h1 className="mt-3 font-technical font-black uppercase leading-[0.85] tracking-tighter text-white text-[15vw] md:text-[9vw] lg:text-[8.5rem]">
          {hero.titleLines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-5 max-w-xl font-body text-base md:text-lg text-white/85"
        >
          <span className="font-semibold text-fw-gold">{hero.subline}</span>
          <br />
          {hero.intro}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href={site.flyerPdf}
            download
            data-testid="hero-download-pdf"
            className="inline-flex items-center gap-2 rounded-full bg-fw-red px-7 py-4 font-technical uppercase tracking-widest text-white text-sm shadow-xl transition-all hover:bg-fw-redHover active:scale-95"
          >
            <Download className="h-4 w-4" /> Flyer herunterladen
          </a>
          <a
            href="#grussworte"
            data-testid="hero-scroll-cta"
            className="inline-flex items-center gap-2 border-b border-fw-gold pb-1 font-technical uppercase tracking-widest text-fw-gold text-sm transition-colors hover:text-white"
          >
            Entdecken <ChevronDown className="h-4 w-4" />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
